import React, { useState, useEffect } from 'react';
import { sendApiRequest } from '../utils';
import { toast } from 'react-toastify';

const MyTotalNetwork = () => {
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);  // Show 10 items per page

  // Filters and Search
  const [activeTab, setActiveTab] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState(1);
  const [searchTerm, setSearchTerm] = useState({
    memberId: '',
    regFrom: '',
    regTo: ''
  });

  // Data and Loading
  const [teamData, setTeamData] = useState({ members: [], stats: { totalMembers: 0, activeMembers: 0, totalNetworkBusiness: 0, currentMonthNetworkBusiness: 0 } });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Event Handlers
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleLevelChange = (e) => {
    setSelectedLevel(Number(e.target.value));
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchTerm(prev => ({ ...prev, [name]: value }));
  };

  // Placeholder exportToCSV function
  const exportToCSV = () => {
    // Implement CSV export logic here
    toast.info('Export to CSV functionality not implemented yet');
  };

  // Data Fetching
  useEffect(() => {
    const fetchTotalNetwork = async () => {
      setLoading(true);
      try {
        const response = await sendApiRequest('api/network/my-total-network', {}, 'GET');
        if (response.data.success) {
          setTeamData(response.data.data);
          // Total pages will be updated by the useEffect when filteredMembers changes
        } else {
          throw new Error(response.message || 'Failed to fetch network data');
        }
      } catch (error) {
        const message = 'Failed to fetch network data';
        setError(message);
        toast.error(message);
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchTotalNetwork();
  }, [itemsPerPage]);

  const filteredMembers = teamData.members.filter((member) => {
    // Filter by search terms
    const matchesSearch = 
      member.memberId.toLowerCase().includes(searchTerm.memberId.toLowerCase());
    
    // Filter by date range if provided
    const matchesDateRange = 
      (!searchTerm.regFrom || member.regDate >= searchTerm.regFrom) &&
      (!searchTerm.regTo || member.regDate <= searchTerm.regTo);
    
    // Filter by active tab
    const matchesStatus = 
      activeTab === 'all' || 
      (activeTab === 'active' && member.status === 'active') || 
      (activeTab === 'inactive' && member.status === 'inactive');
    
    // Filter by level
    const matchesLevel = selectedLevel === 'all' || member.level.toString() === selectedLevel.toString();
    
    return matchesSearch && matchesDateRange && matchesStatus && matchesLevel;
  });

  // Update total pages when filtered members change
  useEffect(() => {
    setTotalPages(Math.ceil(filteredMembers.length / itemsPerPage));
    // Reset to first page when filters change
    setCurrentPage(1);
  }, [filteredMembers.length, itemsPerPage]);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">My Total Network</h1>
        <p className="text-gray-600">View and manage your complete network members</p>
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-md p-4">
          <h3 className="text-sm font-medium text-gray-500">Total Members</h3>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{teamData.stats.totalMembers}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4">
          <h3 className="text-sm font-medium text-gray-500">Total Business</h3>
          <p className="mt-1 text-2xl font-semibold text-blue-600">
            ₹{teamData.stats.totalNetworkBusiness.toLocaleString('en-IN')}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4">
          <h3 className="text-sm font-medium text-gray-500">This Month's Business</h3>
          <p className="mt-1 text-2xl font-semibold text-green-600">
            ₹{teamData.stats.currentMonthNetworkBusiness.toLocaleString('en-IN')}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4">
          <h3 className="text-sm font-medium text-gray-500">Active Members</h3>
          <p className="mt-1 text-2xl font-semibold text-gray-900">
            {teamData.stats.activeMembers}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4">
          <h3 className="text-sm font-medium text-gray-500">Total Levels</h3>
          <p className="mt-1 text-2xl font-semibold text-blue-600">
            {Math.max(...teamData.members.map(m => m.level), 0)}
          </p>
        </div>
      </div>

      {/* Search and Filter Section */}
      {/* <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search Members</label>
            <input
              type="text"
              name="memberId"
              placeholder="Enter Member ID"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              value={searchTerm.memberId}
              onChange={handleSearchChange}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              name="regFrom"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              value={searchTerm.regFrom}
              onChange={handleSearchChange}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              name="regTo"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              value={searchTerm.regTo}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div> */}

      {/* Status Tabs */}
      {/* <div className="flex space-x-2 mb-4">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
        >
          All Members
        </button>
        <button
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === 'active' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
        >
          Active
        </button>
        <button
          onClick={() => setActiveTab('inactive')}
          className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === 'inactive' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
        >
          Inactive
        </button>
      </div> */}

      {/* Action Buttons */}
      {/* <div className="flex space-x-3 mb-6">
        <button className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition transform hover:scale-105 shadow-md">
          Search Members
        </button>
        <button 
          onClick={exportToCSV}
          className="flex items-center bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded-lg transition transform hover:scale-105 shadow-md"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
          Export CSV
        </button>
      </div> */}

      {/* Results Summary */}
      <div className="mb-4 flex justify-between items-center">
        <p className="text-gray-600">
          Showing <span className="font-bold">{filteredMembers.length}</span> of <span className="font-bold">{teamData.stats.totalMembers}</span> members (Level {selectedLevel})
        </p>
      </div>

      {/* Table */}
      <div className="bg-white shadow-md rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  #
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Level
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Business
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  This Month
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reg. Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Act. Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {teamData.members.length > 0 ? (
                teamData.members.map((member, index) => (
                  <tr key={member.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-medium">
                      {member.memberId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {member.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      Level {member.level}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ₹{member.totalBusiness?.toLocaleString('en-IN') || '0'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      ₹{member.currentMonthBusiness?.toLocaleString('en-IN') || '0'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.regDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.actDate || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          member.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {member.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-8 text-gray-500">
                    No members found matching your criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-6 flex justify-center">
        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                page === currentPage
                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
              }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
          >
            Next
          </button>
        </nav>
      </div>
    </div>
  );
};

export default MyTotalNetwork;